import { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { Modal } from "flowbite-react";
import AuthProvider from "../services/auth/AuthProvider/Index";
import CenteredLoader from "../components/CenteredLoader";
import DefaultLayout from "../layouts/default";
import { setUserData } from "../../redux/actions/userDataActions";
import { Navigation, UserNavigation } from "../types";
import ProfileUpdate from "../pages/ProfileUpdate";
import { Button } from "./Form/Button";
import notifications from "../services/notifications";

const AUTO_LOGOUT_STORAGE_KEY = `${process.env.REACT_APP_STORAGE_NAMESPACE}_auto_logout`;

const events = [
  "load",
  "mousemove",
  "mousedown",
  "click",
  "scroll",
  "keypress",
];

type AutoLogoutAlertProps = {
  countdownDuration?: number;
  onStayConnected: () => void;
};

const AutoLogoutAlert: React.FC<AutoLogoutAlertProps> = ({
  countdownDuration = parseInt(`${process.env.REACT_APP_AUTOLOGOUT_COUNTDOWN}`),
  onStayConnected,
}) => {
  const [countdown, setCountdown] = useState<number>(countdownDuration);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (countdown > 0) {
      timerRef.current = setInterval(() => {
        setCountdown(countdown - 1);
      }, 1000);

      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      };
    } else {
      (async () => {
        await AuthProvider.logout();
      })();
    }
  }, [countdown, onStayConnected]);

  return (
    <>
      <Modal show={true}>
        <Modal.Header>Inactivity Timeout Alert</Modal.Header>
        <Modal.Body>
          <div className="w-full mt-6">
            <p>
              Your session is about to expire due to inactivity in...{" "}
              <b>{countdown} seconds</b>.
            </p>
            <br />
            <p>
              If you'd like to continue with your session, click the "Stay
              connected" button below.
            </p>
            <br />
            <p>
              Thank you for choosing our services, and for helping us maintain a
              secure online environment.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="w-full">
            <Button
              label="Stay connected"
              theme="primary"
              size="medium"
              className="block w-full mb-4 md:float-right md:ml-auto md:w-auto md:ml-4 mb:mb-0"
              onClick={() => {
                if (timerRef.current) {
                  clearInterval(timerRef.current);
                }

                localStorage.setItem(AUTO_LOGOUT_STORAGE_KEY, String(Date.now()));

                onStayConnected();
              }}
            />
            <Button
              label="Log Out"
              theme="secondary"
              size="medium"
              className="block w-full md:float-right md:ml-auto md:w-auto"
              onClick={async () => {
                if (timerRef.current) {
                  clearInterval(timerRef.current);
                }

                await AuthProvider.logout();
              }}
            />
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const AuthenticatedRoute = ({
  component: Component,
  title = "",
  navigation,
  userNavigation,
  profileSetupPage: ProfileSetupPage = ProfileUpdate,
}: {
  component: any;
  title: string;
  navigation?: Navigation;
  userNavigation?: UserNavigation;
  profileSetupPage?: any;
}) => {
  const [loading, setLoading] = useState(true);
  const [loggedIn, setLoggedIn] = useState(false);
  const [initialized, setInitialized] = useState(true);
  const [showAutoLogoutAlert, setShowAutoLogoutAlert] = useState(false);
  const dispatch = useDispatch();
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    (async () => {
      const dispatchSetUserData = (value: any) => {
        dispatch(setUserData(value));
      };

      console.log("getting identity");
      const identity = await AuthProvider.getIdentity();

      if (identity) {
        dispatchSetUserData(identity);
        setLoggedIn(true);
        setInitialized(
          true
        );

        const resetAutoLogoutAlertTimer = () => {
          if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
          }
        };

        const autoLogoutAlertTimer = () => {
          timerRef.current = setTimeout(async () => {
            resetAutoLogoutAlertTimer();
            Object.values(events).forEach((item) => {
              window.removeEventListener(item, () => {
                resetAutoLogoutAlertTimer();
                autoLogoutAlertTimer();
              });
            });

            const minimumSessionLifespan =
              await AuthProvider.getMinimumSessionLifespan();

            if (
              minimumSessionLifespan >
              parseInt(
                `${process.env.REACT_APP_AUTOLOGOUT_MIN_SECS_SESSION_LIFESPAN}`
              )
            ) {
              setShowAutoLogoutAlert(true);
            }
          }, parseInt(`${process.env.REACT_APP_AUTOLOGOUT_IN_MS}`));
        };

        Object.values(events).forEach((item) => {
          window.addEventListener(item, () => {
            resetAutoLogoutAlertTimer();
            autoLogoutAlertTimer();
          });
        });

        window.addEventListener('storage', (event) => {
          if (event.key === AUTO_LOGOUT_STORAGE_KEY) {
            resetAutoLogoutAlertTimer();
            autoLogoutAlertTimer();
            setShowAutoLogoutAlert(false);
          }
        });

        autoLogoutAlertTimer();
        setLoading(false);
      }
    })();
  }, [dispatch]);

  useEffect(() => {
    if (loggedIn && initialized) {
      document.title = `JackAtHome ${title ? `| ${title}` : ""}`;
    }

    if (loggedIn && !initialized) {
      document.title = `JackAtHome ${title ? `| Account setup` : ""}`;

      notifications.warning({message: "Your profile information is not complete or is unverified, please complete and/or complete your profile information."});
    }
  }, [loggedIn, initialized, title]);

  useEffect(() => {
    if (loggedIn && !loading) {
      notifications.onReload();
    }
  }, [loggedIn, loading]);

  const handleStayConnected = () => {
    setShowAutoLogoutAlert(false);
  };

  if (!loading && loggedIn && initialized) {
    return (
      <DefaultLayout navigation={navigation} userNavigation={userNavigation}>
        <>
          {showAutoLogoutAlert && (
            <AutoLogoutAlert onStayConnected={handleStayConnected} />
          )}
          <Component />
        </>
      </DefaultLayout>
    );
  }

  if (!loading && loggedIn && !initialized) {
    return <ProfileSetupPage isAccountSetup={true} />;
  }

  return <CenteredLoader />;
};

export default AuthenticatedRoute;
